/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import './chatPanel.css';
import OpenAIService, { Message } from '../../services/openai.service';
import { UserProfile } from '../../services/user.service';
import MessageList from './MessageList';
import XervixLoader from '../ui/Xervix-loader';
import { resetCitiesShownState } from './AgentMessage';
import EnergySphere from '../ui/animation-xervix';

interface ChatPanelProps {
  profileImage?: string;
  isAuthenticated?: boolean;
  userProfile?: UserProfile | null;
  selectedConversation?: any;
  showWelcome?: boolean;
  onHideWelcome?: () => void;
  isMobile?: boolean;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  profileImage: propProfileImage,
  isAuthenticated: propIsAuthenticated,
  userProfile: propUserProfile,
  selectedConversation,
  showWelcome: propShowWelcome = true,
  onHideWelcome,
  isMobile = false
}) => {
  const [message, setMessage] = useState('');
  const [showWelcome, setShowWelcome] = useState(propShowWelcome);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(propIsAuthenticated || false);
  const [profileImage, setProfileImage] = useState<string | undefined>(propProfileImage);

  // Función helper para ocultar la pantalla de bienvenida
  const hideWelcomeScreen = () => {
    setShowWelcome(false);
    if (onHideWelcome) {
      onHideWelcome();
    }
  };


  // Actualizar el estado cuando cambien las props
  useEffect(() => {
    if (propProfileImage !== undefined) {
      setProfileImage(propProfileImage);
    }

    if (propIsAuthenticated !== undefined) {
      setIsAuthenticated(propIsAuthenticated);
    }

    // Si no estamos autenticados, mostrar mensaje de error
    if (propIsAuthenticated === false) {
      setError('Debes iniciar sesión para usar el chat');
    } else if (propIsAuthenticated === true) {
      setError(null);
    }
  }, [propProfileImage, propIsAuthenticated, propUserProfile]);

  // Efecto para actualizar el estado de showWelcome cuando cambie la prop
  useEffect(() => {
    setShowWelcome(propShowWelcome);
  }, [propShowWelcome]);

  // Efecto para escuchar el evento de reinicio de conversación
  useEffect(() => {
    const handleResetConversation = () => {
      // Reiniciar el estado de los mensajes
      setMessages([]);
      // Reiniciar el ID de conversación
      setConversationId(null);
      // Reiniciar el estado de error
      setError(null);
      // Reiniciar el estado de carga
      setLoading(false);
      // Reiniciar el campo de mensaje
      setMessage('');
      // Reiniciar el estado de las ciudades mostradas en AgentMessage
      resetCitiesShownState();
    };

    // Registrar el evento
    window.addEventListener('reset-conversation', handleResetConversation);

    // Limpiar el evento al desmontar
    return () => {
      window.removeEventListener('reset-conversation', handleResetConversation);
    };
  }, []);

  // Efecto para enviar un mensaje inicial automáticamente si existe en localStorage
  useEffect(() => {
    const checkAndSendInitialMessage = async () => {
      // Verificar si el usuario está autenticado y si hay un mensaje inicial
      const initialMessage = localStorage.getItem('initial_chat_message');

      if (isAuthenticated && initialMessage && messages.length === 0 && !selectedConversation) {
        // Eliminar el mensaje inicial de localStorage para no enviarlo múltiples veces
        localStorage.removeItem('initial_chat_message');

        // Crear un objeto de mensaje para el usuario
        const userMessage: Message = {
          role: 'user',
          content: initialMessage
        };

        // Agregar el mensaje del usuario a la conversación para que se muestre en la interfaz
        setMessages(prevMessages => [...prevMessages, userMessage]);

        // Ocultar la pantalla de bienvenida
        hideWelcomeScreen();

        // Esperar un momento para asegurar que la interfaz esté lista y se muestre el mensaje del usuario
        setTimeout(() => {
          // Enviar el mensaje inicial automáticamente al backend, indicando que ya se mostró el mensaje del usuario
          handleSendMessageWithContent(initialMessage, true);
        }, 1000);
      }
    };

    checkAndSendInitialMessage();
  }, [isAuthenticated, messages.length, selectedConversation]);

  // Efecto para cargar la conversación seleccionada
  useEffect(() => {
    if (selectedConversation) {
      // Ocultar la pantalla de bienvenida
      hideWelcomeScreen();

      // Extraer los mensajes de la conversación
      if (selectedConversation.conversationHistory && Array.isArray(selectedConversation.conversationHistory)) {
        // Convertir el historial de conversación al formato esperado por MessageList
        const formattedMessages: Message[] = selectedConversation.conversationHistory.map((msg: any) => ({
          role: msg.role,
          content: msg.content
        }));

        // Establecer los mensajes
        setMessages(formattedMessages);

        // Establecer el ID de conversación si existe
        if (selectedConversation.id) {
          setConversationId(selectedConversation.id);
        }
      }
    }
  }, [selectedConversation]);

  const handleSendMessage = async () => {
    if (message.trim()) {
      // Verificar si el usuario está autenticado
      if (!isAuthenticated) {
        setError('Debes iniciar sesión para usar el chat');
        return;
      }

      // Ocultar la pantalla de bienvenida
      hideWelcomeScreen();

      // FORZAR nueva conversación si es el primer mensaje después de recargar
      if (messages.length === 0 && conversationId) {
        setConversationId(null);
      }

      // Agregar el mensaje del usuario a la conversación
      const userMessage: Message = {
        role: 'user',
        content: message.trim()
      };

      // Actualizar el estado con el nuevo mensaje
      setMessages(prevMessages => [...prevMessages, userMessage]);
      setMessage('');
      setLoading(true);
      setError(null);

      // Establecer un timeout más largo para la respuesta
      const timeoutDuration = 120000; // 120 segundos
      let timeoutId: NodeJS.Timeout | null = null;

      // Crear una promesa que se rechaza después del timeout
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('La solicitud ha tardado demasiado tiempo. Intente nuevamente.'));
        }, timeoutDuration);
      });

      try {
        // Si no hay ID de conversación O no hay mensajes previos, iniciar una nueva
        if (!conversationId || messages.length <= 1) {
          // Resetear el estado para la nueva conversación
          resetCitiesShownState();

          try {
            // Usar Promise.race para implementar un timeout personalizado
            const result = await Promise.race([
              OpenAIService.startConversation(userMessage.content),
              timeoutPromise
            ]) as any;

            // Si llegamos aquí, la solicitud se completó antes del timeout
            if (timeoutId) clearTimeout(timeoutId);

            // Guardar el ID de conversación para futuros mensajes si existe
            if (result && result.conversationId) {
              setConversationId(result.conversationId);
            } else if (result && typeof result === 'object') {
              // Intentar encontrar el ID de conversación en la respuesta
              const resultAny = result as any;
              const possibleId = resultAny.id || result.conversationId;
              if (possibleId) {
                setConversationId(possibleId);
              } else {
                // Generar un ID temporal si no se encuentra
                const tempId = `temp-${Date.now()}`;
                setConversationId(tempId);
              }
            }

            // Extraer el contenido del mensaje de la respuesta, manejando diferentes estructuras posibles
            let messageContent = '';

            // Verificar todas las posibles ubicaciones del contenido del mensaje
            const resultAny = result as any;

            if (result && result.content) {
              messageContent = result.content;
            } else if (resultAny && typeof resultAny.message === 'string') {
              messageContent = resultAny.message;
            } else if (resultAny && resultAny.data && resultAny.data.content) {
              messageContent = resultAny.data.content;
            } else if (typeof result === 'string') {
              messageContent = result;
            } else if (result && typeof result === 'object') {
              // Buscar cualquier propiedad que pueda contener el mensaje
              const possibleContentKeys = ['text', 'message', 'response', 'answer', 'reply'];
              for (const key of possibleContentKeys) {
                if (resultAny[key] && typeof resultAny[key] === 'string') {
                  messageContent = resultAny[key];
                  break;
                }
              }

              if (!messageContent) {
                // Si no encontramos el contenido, convertir todo el objeto a string
                messageContent = JSON.stringify(result);
              }
            }

            if (!messageContent) {
              // Si no podemos encontrar el contenido, mostrar un mensaje genérico
              messageContent = 'No se pudo obtener una respuesta del asistente';
              console.error('No se pudo encontrar el contenido del mensaje en la respuesta:', result);
            }

            const assistantMessage: Message = {
              role: 'assistant',
              content: messageContent
            };

            setMessages(prevMessages => [...prevMessages, assistantMessage]);
          } catch (innerError: any) {
            console.error('Error al procesar la respuesta del servidor:', innerError);

            // Mensaje de error más descriptivo según el tipo de error
            if (innerError.message && innerError.message.includes('tardado demasiado tiempo')) {
              setError('La respuesta está tardando más de lo esperado. El servidor podría estar ocupado. Por favor, intenta nuevamente.');
            } else if (innerError.response && innerError.response.status) {
              setError(`Error del servidor (${innerError.response.status}). Por favor, intenta nuevamente.`);
            } else {
              setError('Error al procesar la respuesta. Por favor, intenta nuevamente.');
            }
          }
        } else {
          // Validar que el conversationId sea válido antes de usarlo
          if (!conversationId || conversationId.startsWith('temp-')) {
            // Si el ID no es válido, iniciar una nueva conversación
            const result = await Promise.race([
              OpenAIService.startConversation(userMessage.content),
              timeoutPromise
            ]) as any;

            if (timeoutId) clearTimeout(timeoutId);

            if (result && result.conversationId) {
              setConversationId(result.conversationId);
            }

            // Procesar respuesta...
            let messageContent = '';
            const resultAny = result as any;

            if (result && result.content) {
              messageContent = result.content;
            } else if (resultAny && typeof resultAny.message === 'string') {
              messageContent = resultAny.message;
            } else if (typeof result === 'string') {
              messageContent = result;
            }

            if (!messageContent) {
              messageContent = 'No se pudo obtener una respuesta del asistente';
            }

            const assistantMessage: Message = {
              role: 'assistant',
              content: messageContent
            };

            setMessages(prevMessages => [...prevMessages, assistantMessage]);
            return;
          }

          try {
            // Usar Promise.race para implementar un timeout personalizado
            const response = await Promise.race([
              OpenAIService.sendMessage(conversationId, userMessage.content),
              timeoutPromise
            ]) as any;

            // Si llegamos aquí, la solicitud se completó antes del timeout
            if (timeoutId) clearTimeout(timeoutId);

            // Extraer el contenido del mensaje de la respuesta, manejando diferentes estructuras posibles
            let messageContent = '';

            // Verificar todas las posibles ubicaciones del contenido del mensaje
            const responseAny = response as any;

            if (response && response.content) {
              messageContent = response.content;
            } else if (responseAny && typeof responseAny.message === 'string') {
              messageContent = responseAny.message;
            } else if (responseAny && responseAny.data && responseAny.data.content) {
              messageContent = responseAny.data.content;
            } else if (typeof response === 'string') {
              messageContent = response;
            } else if (response && typeof response === 'object') {
              // Buscar cualquier propiedad que pueda contener el mensaje
              const possibleContentKeys = ['text', 'message', 'response', 'answer', 'reply'];
              for (const key of possibleContentKeys) {
                if (responseAny[key] && typeof responseAny[key] === 'string') {
                  messageContent = responseAny[key];
                  break;
                }
              }

              if (!messageContent) {
                // Si no encontramos el contenido, convertir todo el objeto a string
                messageContent = JSON.stringify(response);
              }
            }

            if (!messageContent) {
              // Si no podemos encontrar el contenido, mostrar un mensaje genérico
              messageContent = 'No se pudo obtener una respuesta del asistente';
              console.error('No se pudo encontrar el contenido del mensaje en la respuesta:', response);
            }

            const assistantMessage: Message = {
              role: 'assistant',
              content: messageContent
            };

            setMessages(prevMessages => [...prevMessages, assistantMessage]);
          } catch (innerError: any) {
            console.error('Error al procesar la respuesta del servidor:', innerError);

            // Mensaje de error más descriptivo según el tipo de error
            if (innerError.message && innerError.message.includes('tardado demasiado tiempo')) {
              setError('La respuesta está tardando más de lo esperado. El servidor podría estar ocupado. Por favor, intenta nuevamente.');
            } else if (innerError.response && innerError.response.status) {
              setError(`Error del servidor (${innerError.response.status}). Por favor, intenta nuevamente.`);
            } else {
              setError('Error al procesar la respuesta. Por favor, intenta nuevamente.');
            }
          }
        }
      } catch (err: any) {
        console.error('Error al enviar mensaje:', err);

        // Verificar si es un error de conversación no encontrada
        if (err.message && err.message.includes('not found')) {
          setConversationId(null);
          setError('La conversación ha expirado. Por favor, recarga la página para iniciar una nueva conversación.');
        }
        // Verificar si es un error de autenticación
        else if (err.response && err.response.status === 401) {
          setError('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
          setIsAuthenticated(false);
        } else if (err.message && err.message.includes('tardado demasiado tiempo')) {
          setError('La respuesta está tardando más de lo esperado. El servidor podría estar ocupado. Por favor, intenta nuevamente.');
        } else {
          setError('Error al enviar el mensaje. Por favor, intenta nuevamente.');
        }
      } finally {
        // Limpiar el timeout si aún existe
        if (timeoutId) clearTimeout(timeoutId);
        setLoading(false);
      }
    }
  };

  // Manejar la selección de una ciudad desde las cards
  const handleCitySelection = (cityName: string) => {
    // NO establecemos el mensaje en el input
    // Eliminamos: setMessage(cityName);

    // Crear un objeto de mensaje temporal para mostrar inmediatamente la selección del usuario
    const userCityMessage: Message = {
      role: 'user',
      content: cityName
    };

    // Agregar el mensaje al historial
    setMessages(prevMessages => [...prevMessages, userCityMessage]);

    // Enviar el mensaje al backend
    setTimeout(() => {
      // Usamos setTimeout para asegurar que el mensaje del usuario se muestre primero
      handleSendMessageWithContent(cityName);
    }, 100);
  };

  // Función para enviar un mensaje del agente sin necesidad de una respuesta del usuario
  const sendAgentFinalMessage = () => {
    if (!conversationId) return;

    // Mensaje de despedida emotivo después de mostrar la tarjeta de contacto
    const finalMessage: Message = {
      role: 'assistant',
      content: '¡Excelente elección! 🎉 Ha sido un placer ayudarte a encontrar el profesional ideal para tu necesidad. Recuerda que estamos aquí para cualquier consulta adicional que tengas. ¡Te deseamos una experiencia extraordinaria con nuestros servicios! 💯'
    };
    setMessages(prevMessages => [...prevMessages, finalMessage]);
  };

  // Versión modificada de handleSendMessage que acepta un contenido específico
  // skipUserMessage: si es true, no agrega el mensaje del usuario a la interfaz (porque ya se hizo manualmente)
  const handleSendMessageWithContent = async (content: string, skipUserMessage: boolean = false) => {
    // Cuando se selecciona una ciudad, no necesitamos resetear el estado
    // ya que queremos que no se vuelvan a mostrar las cards en esta conversación
    if (!content.trim()) return;

    try {
      setLoading(true);

      // Verificar si el usuario está autenticado
      if (!isAuthenticated) {
        setError('Debes iniciar sesión para usar el chat');
        return;
      }

      // Ocultar la pantalla de bienvenida
      hideWelcomeScreen();

      // Crear el mensaje del usuario con el contenido proporcionado
      /* const userMessage: Message = {
        role: 'user',
        content: content
      }; */

      // No agregamos el mensaje del usuario aquí porque ya se agregó en handleCitySelection

      try {
        if (!conversationId) {
          // Resetear el estado de las ciudades mostradas para la nueva conversación
          resetCitiesShownState();
          // Iniciar una nueva conversación
          const result = await OpenAIService.startConversation(content);
          setConversationId(result.conversationId);

          // Procesar la respuesta igual que en handleSendMessage
          let messageContent = '';
          if (result.content) {
            messageContent = result.content;
          } else if (typeof result === 'string') {
            messageContent = result;
          } else {
            messageContent = 'No se pudo obtener una respuesta del asistente';
          }

          const assistantMessage: Message = {
            role: 'assistant',
            content: messageContent
          };

          setMessages(prevMessages => [...prevMessages, assistantMessage]);
        } else {
          // Enviar mensaje a conversación existente
          const response = await OpenAIService.sendMessage(conversationId, content);

          let messageContent = '';
          if (response.content) {
            messageContent = response.content;
          } else if (typeof response === 'string') {
            messageContent = response;
          } else {
            messageContent = 'No se pudo obtener una respuesta del asistente';
          }

          const assistantMessage: Message = {
            role: 'assistant',
            content: messageContent
          };

          setMessages(prevMessages => [...prevMessages, assistantMessage]);
        }
      } catch (err: any) {
        if (err.response && err.response.status === 401) {
          setError('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
          setIsAuthenticated(false);
        } else {
          setError('Error al enviar el mensaje. Por favor, intenta nuevamente.');
        }
      } finally {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="grid grid-rows-[auto_1fr_auto] h-full bg-xervix-bg-secondary dark:bg-xervix-bg-secondary-dark rounded-r-lg">
      {/* Encabezado del chat */}
      <div className="flex justify-center py-4 border-gray-700">
        <div className="text-3xl font-bold text-xervix-primary">XERVIX</div>
      </div>

      {/* Área de mensajes - con scroll oculto visualmente */}
      <div className="overflow-y-auto hide-scrollbar p-0 md-p-6 bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-xl mx-4 md:mx-8">
        {showWelcome ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="p-0 md:p-6 rounded-xl shadow-lg max-w-3xl w-full text-center">
              
              <div className="flex justify-center mb-6 mt-1 md:mt-8">
                <EnergySphere size={isMobile ? 120 : 200} />
              </div>
              
              <h1 className="text-2xl md:text-4xl font-bold text-xervix-primary mb-6">¡Bienvenido a XERVIX!</h1>
              
              <p className="text-sm md:text-xl text-xervix-text-primary dark:text-xervix-text-primary-dark mb-12">
                Tu asistente virtual para contratar servicios profesionales
              </p>              
              
                <div className="flex items-center mb-4 px-2 w-full justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-xervix-primary mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  <h2 className="text-sm md:text-xl font-semibold text-xervix-text-primary dark:text-xervix-text-primary-dark">Escribe tu solicitud en el campo de abajo y comencemos 👇</h2>
                </div>         
             
            </div>
          </div>
        ) : (
          <div className="h-full">
            {messages.length === 0 ? (
              <div className="text-xervix-text-secondary dark:text-xervix-text-secondary-dark text-center mb-4">
                <p className="mb-2">¿En qué puedo ayudarte hoy?</p>
                <p className="text-sm text-xervix-text-muted dark:text-xervix-text-muted-dark">
                  Puedes preguntarme sobre servicios, cotizaciones o solicitar un profesional.
                </p>
              </div>
            ) : (
              <MessageList
                messages={messages}
                loading={loading}
                profileImage={profileImage}
                onSendMessage={handleCitySelection}
                conversationId={conversationId}
                onShowContactCard={sendAgentFinalMessage}
              />
            )}

            {error && (
              <div className="bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-100 p-3 rounded-lg mb-4">
                {error}
              </div>
            )}
          </div>
        )}

      </div>


          {/* Área de entrada de mensaje - fija en la parte inferior */}
      <div className="px-4 md:px-8 py-4">
        <div className="border-xervix-primary border-2 flex items-center bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-lg px-2 py-3 ">
          <input
            type="text"
            placeholder="Aquí puedes solicitar servicios, aclarar dudas, cotizar etc."
            className=" flex-1 bg-transparent border-none text-xervix-text-primary dark:text-xervix-text-primary-dark focus:outline-none px-3 py-2"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
          />
          <button
            className={`p-2 rounded-md text-white ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-xervix-primary hover:bg-red-500 transition-colors'}`}
            onClick={handleSendMessage}
            disabled={loading || !message.trim()}
          >
            {loading ? (
              <div className="h-2 w-2 flex items-center justify-center">
                <XervixLoader />
              </div>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
